import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { appApi, useListCompanyCategoriesQuery } from "../../../store/appApi";
import {
  createCompanyCategoryApi,
  deleteCompanyCategoryApi,
} from "../../../services/company";

const CompanyCategory = () => {
  const dispatch = useDispatch();

  // ── GET: RTK Query owns the list — auto-caches and re-fetches on tag invalidation
  const { data: catResponse, isLoading: loadingList } =
    useListCompanyCategoriesQuery();
  const categories = Array.isArray(catResponse?.data) ? catResponse.data : [];

  // ── Local form state for the POST mutation ────────────────────────────────
  const [categoryName, setCategoryName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // ── POST: direct service call, then bust the RTK cache ───────────────────
  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = categoryName.trim();
    if (!trimmed) {
      setError("Category name is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await createCompanyCategoryApi(trimmed);
      setCategoryName("");
      dispatch(appApi.util.invalidateTags(["CompanyCategories"]));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  // ── DELETE: direct service call, then bust the RTK cache ─────────────────
  const handleDelete = async (cat) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete category?",
      text: `"${cat.name}" will be removed from the category list.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;

    const catId = cat._id || cat.id;
    try {
      setDeletingId(catId);
      await deleteCompanyCategoryApi(catId);
      dispatch(appApi.util.invalidateTags(["CompanyCategories"]));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to delete category.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Consultant Categories" motherMenu="Consultants" />
      <div className="row mb-3">
        <div className="col-12 d-flex flex-wrap gap-2">
          <Link
            to="/all-consultants"
            className="btn btn-sm btn-outline-primary"
          >
            All Categories
          </Link>
          <Link to="/add-company" className="btn btn-sm btn-primary">
            Add Company
          </Link>
        </div>
      </div>

      <div className="row">
        {/* ── Add form ── */}
        <div className="col-xl-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Add Company Category</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleAdd}>
                <div className="mb-3">
                  <label className="form-label">Category Name</label>
                  <input
                    type="text"
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    placeholder="e.g. Hotels/Real-Estate"
                    value={categoryName}
                    onChange={(e) => {
                      setCategoryName(e.target.value);
                      setError("");
                    }}
                  />
                  {error && <div className="invalid-feedback">{error}</div>}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Add Category"}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── Category list ── */}
        <div className="col-xl-8">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">All Categories</h5>
            </div>
            <div className="card-body p-0">
              {loadingList ? (
                <div className="p-4 text-center text-muted">
                  Loading categories...
                </div>
              ) : categories.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  No categories found.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered verticle-middle mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((cat, i) => {
                        const catId = cat._id || cat.id;
                        return (
                          <tr key={catId}>
                            <td>{i + 1}</td>
                            <td style={{ textTransform: "capitalize" }}>
                              {cat.name}
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-danger"
                                disabled={deletingId === catId}
                                onClick={() => handleDelete(cat)}
                              >
                                {deletingId === catId
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyCategory;
